<template>
  <router-link class="navListItem"
               :class="[linkSelected, { withIdent: showIdent }]" :to="to" @click="handleClick">
    <div class="ident" v-if="showIdent">
      <UserIdent class="avatar" :user="userIdent" :altDisplayName="formattedName"
                 :showOnlineStatus="!isGroupChat" :title="isGroupChat ? formattedName : undefined"
                 :displayNumber="displayNumber"></UserIdent>
    </div>
    <div v-else class="dot"></div>
    <div class="itemText">
      <div class="line" :class="{hasNotification: !!numNotifications}" :title="formattedName">
        <div class="nameAndStatus">
          <div class="name">
            <TextWithHighlightedTerm :text="formattedName" :highlightRegex="highlightRegex"
                                     :textWithHighlights="textWithHighlights">
            </TextWithHighlightedTerm>
          </div>
          <UserStatusIcon v-if="userStatusForUser" class="userStatus"
                          :emoji="userStatusForUser.emoji" :text="userStatusForUser.text">
          </UserStatusIcon>
        </div>
        <NotificationDot v-if="!isNotificationsSlotPopulated && !!numNotifications"
                         :count="formatNumMessages(numNotifications)"></NotificationDot>
      </div>
      <div class="line" v-if="subtext">
        <div class="subtext" v-text="subtext" v-if="subtext"></div>
      </div>
    </div>
    <div class="date" v-if="formattedDate" v-text="formattedDate"></div>
    <slot name="notification"></slot>
  </router-link>
</template>

<script setup lang="ts">
import { computed, ComputedRef, useSlots } from 'vue';
import { RouteLocationRaw } from 'vue-router';

import NotificationDot from '@/components/NotificationDot.vue';
import TextWithHighlightedTerm from '@/components/search/TextWithHighlightedTerm.vue';
import UserIdent from '@/components/UserIdent.vue';
import { TextWithHighlight } from '@/data/datatypes/TextWithHighlight';
import { Track } from '@/data/datatypes/Track';
import { LimitedUserDetails } from '@/data/datatypes/UserDetails';
import { UserStatus } from '@/data/datatypes/UserStatus';
import { getChatName } from '@/data/helpers/ChatHelper';
import { getRecentActivityDate } from '@/data/helpers/DateHelper';
import { formatNotificationCount } from '@/data/helpers/TrackHelper';
import { useUserStore } from '@/stores/User';
import UserStatusIcon from '@/UserStatusIcon.vue';

const userStore = useUserStore();

const slots = useSlots();

interface Props {
  to: RouteLocationRaw;
  name?: string | null;
  subtext?: string | null;
  // e.g. user status for direct chat
  online?: boolean | null;
  // e.g. new message
  numNotifications?: number | null;
  displayDate?: number | null;
  date?: Date | null;
  // the userID of the user whose status we want to display
  userStatusUserId?: string | null;
  // This will override the default selection highlighting based on the route specified in the 'to' prop
  selected?: boolean | null;
  track?: Track | null;
  user?: LimitedUserDetails | null;
  hideIdent?: boolean;
  highlightRegex?: RegExp;
  textWithHighlights?: TextWithHighlight[];
}

const props = withDefaults(defineProps<Props>(), {
  name: null,
  subtext: null,
  online: null,
  numNotifications: null,
  displayDate: null,
  date: null,
  userStatusUserId: null,
  selected: null,
  track: null,
  user: null,
  hideIdent: false,
});

interface Emits {
  (e: 'click', event: MouseEvent): void,
}
const emit = defineEmits<Emits>();

const currentUserId: ComputedRef<string> = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return userStore.currentUserId!;
});

const showIdent: ComputedRef<boolean> = computed(() => {
  return !props.hideIdent && (isChat.value || isUser.value || !!props.name);
});

const linkSelected: ComputedRef<string | null> = computed(() => {
  // If 'selected' hasn't been specified, then let it default to the route matching for the 'to' prop
  if (props.selected === undefined || props.selected === null) {
    return null;
  }

  return props.selected ? 'selected' : 'notSelected';
});

const isChat: ComputedRef<boolean> = computed(() => {
  if (props.track) {
    return isPrivateChat(props.track);
  }
  return false;
});

const isUser: ComputedRef<boolean> = computed(() => {
  return !!props.user;
});

const isGroupChat: ComputedRef<boolean> = computed(() => {
  let groupChat: boolean = false;
  if (isChat.value && props.track?.typeMetadata) {
    const commaIndex = props.track.typeMetadata.indexOf(',');
    groupChat = !!(commaIndex && commaIndex !== props.track.typeMetadata.lastIndexOf(','));
  }
  return groupChat;
});

const userStatusForUser: ComputedRef<UserStatus | null> = computed(() => {
  if (props.userStatusUserId) {
    return userStore.userStatusesForUsers.get(props.userStatusUserId) ?? null;
  }
  return null;
});

const isNotificationsSlotPopulated: ComputedRef<boolean> = computed(() => {
  return !!slots.notification;
});

const formattedName: ComputedRef<string> = computed(() => {
  if (props.name) {
    return props.name;
  }
  if (props.track) {
    return getChatName(props.track, currentUserId.value, userStore.displayDetailsForUsers);
  }
  return '';
});

const formattedDate: ComputedRef<string | null> = computed(() => {
  const parsedDate: Date | null = props.displayDate ? new Date(props.displayDate) : props.date;
  if (!parsedDate) {
    return null;
  }

  return getRecentActivityDate(parsedDate);
});

const userIdent: ComputedRef<LimitedUserDetails | null> = computed(() => {
  if (props.user) {
    return props.user;
  }
  if (!isChat.value) {
    return null;
  }
  if (privateChatUsers.value.length) {
    return privateChatUsers.value[0];
  }
  return null;
});

const privateChatUsers: ComputedRef<LimitedUserDetails[]> = computed(() => {
  if (props.track) {
    return getPrivateChatUsers(props.track, currentUserId.value, userStore.displayDetailsForUsers);
  }
  return [];
});

const displayNumber: ComputedRef<number | undefined> = computed(() => {
  if (isGroupChat.value) {
    return privateChatUsers.value.length;
  }
  return undefined;
});

function handleClick(event: MouseEvent): MouseEvent {
  emit('click', event);
  return event;
}

function formatNumMessages(num: number): string | null {
  return formatNotificationCount(num);
}
</script>

<style scoped lang="scss">
$size-icon: 24px;
$size-icon-font: 12px;
$size-text: 0.75rem;
$size-subtext: 12px;
$weight-text: $weight-normal;
$weight-subtext: $weight-light;

.navList .navListItem {
  padding: 0 0.75rem 0 1.5rem;
}

.navListItem {
  display: flex;
  font-weight: $weight-text;
  color: $colour-text-primary-emphasis;
  align-items: center;
  padding: 0.5rem 0;

  &:not(.withIdent) {
    height: 1.5rem;
    .itemText {
      padding-top: 0;
    }
  }

  &:hover {
    background-color: $colour-background-mid;
  }

  // router-link-active is present when the router points to the component
  // so use that to help with component auto-highlighting
  &.selected, &.router-link-active:not(.notSelected) {
    font-weight: $weight-bold;
  }

  &:first-child {
    margin-top: 0;
  }
}
.notification {
  flex: 0 0 0.5rem;
  border-radius: 50%;
  width: 0.5rem;
  height: 0.5rem;
  align-self: center;
  background-color: $colour-notification;
  margin-left: 0.5rem;
}

.icon {
  width: $size-icon;
  min-width: $size-icon;
  height: $size-icon;
  font-size: $size-icon-font;
  text-align: center;
  line-height: $size-icon;
  // TODO: This 7px is presumably wrong in the designs
  margin: auto 7px auto 0;
  flex: 0 0;
  text-transform: uppercase;
  overflow: hidden;
  color: $colour-icon-primary;

  &.chat, &.user {
    background-color: $colour-icon-primary;
    color: $colour-text-contrast;
    border-radius: 50%;
  }
  &.track {
    color: $colour-icon-primary;
    border: 1px solid $colour-icon-primary;
    border-radius: 4px;
  }
  &.group {
    font-size: $size-icon;
    background-color: $colour-background-light;
    color: $colour-icon-primary;

    .brandIconContainer {
      width: 100%;
      height: 100%;
    }
  }
}

.userStatus {
  font-size: 0.625rem;
  flex: 0 0;

  :deep(.userStatusEmoji) {
    min-width: 0.875rem;
    transform: translateY(-10%) scale(1.3);
    // for some reason FF requires the opposite sign on the translateY:
    -moz-transform: translateY(10%) scale(1.3);
  }
  :deep(.userStatusPopup) {
    font-size: 0.875rem;
  }
}

.itemText {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: center;
  padding-top: 0.125rem;

  :not(:first-child) {
    margin-top: 1px;
  }

  .line {
    display: flex;
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-content: center;

    &.hasNotification {
      font-weight: $weight-semi-bold;
    }
  }

  .nameAndStatus {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    flex: 0 1 auto;
    overflow: hidden;
    gap: 0.375rem;
    padding-right: 0.375rem;
  }

  .name {
    flex: 1 1;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    line-height: 1.25rem;
    .highlight {
      background-color: $colour-background-mid;
    }
  }
  .subtext {
    flex: 1 1;
    font-size: $size-subtext;
    font-weight: $weight-subtext;
    // ffs - this is a random number that happens to make the designed picture look nice
    height: 17px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    padding-right: 0.25rem;
  }
}

.date {
  font-size: $size-subtext;
  font-weight: $weight-subtext;
  margin-left: 0.5rem;
}

.ident {
  display: flex;
  .avatar {
    display: flex;
    align-self: flex-start;
    margin-right: 0.625rem;

    &.logo {
      border-radius: 50%;
    }

    &.logo, :deep(.initialWrapper) {
      border: 1px solid white;

      // Safari doesn't handle border-radius on outline, so renders these as square. Hack around it until Safari is good
      // This block is the preferred way of working:
      // future at some point
      // outline-width: 1px;
      // outline-style: solid;
      // outline-color: $color-menu-deselected;
      // .router-link-exact-active & {
      //   outline-width: 2px;
      //   outline-color: $colour-border-emphasis;
      // }
      // Start of Safari outline border-radius hack
      overflow: visible;
      position: relative;
      &::before {
        content: "";
        position: absolute;
        top: -2px;
        right: -2px;
        bottom: -2px;
        left: -2px;
        border: 1px solid $color-menu-deselected;
        border-radius: 50%;
      }
      .router-link-exact-active & {
        &::before {
          top: -3px;
          right: -3px;
          bottom: -3px;
          left: -3px;
          border-width: 2px;
          border-color: $colour-border-emphasis;
        }
      }
      img {
        border-radius: 50%;
      }
      // End of Safari outline border-radius hack
    }
  }
}

.dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: $color-menu-deselected;
  margin-right: 1rem;
  .selected &, .router-link-exact-active & {
    background-color: $color-menu-highlight;
  }
}

</style>
